import React, { useEffect, useState, Component } from 'react';
import { ChevronLeft } from 'lucide-react';
import { UnleashWizard, AngelData } from './UnleashWizard';
import { ScenarioWizard, ScenarioData } from './scenario/ScenarioWizard';
import { UnleashFeatureToggle } from './UnleashFeatureToggle';
import { AngelCreatedSuccess } from './AngelCreatedSuccess';
interface UnleashFeatureProps {
  onBack: () => void;
  onAngelCreated: (angelData: AngelData) => void;
}
export const UnleashFeature: React.FC<UnleashFeatureProps> = ({
  onBack,
  onAngelCreated
}) => {
  // Add state to track which feature is active
  const [activeFeature, setActiveFeature] = useState<'angel' | 'scenario'>('angel');
  const [showWizard, setShowWizard] = useState(false);
  const [completedAngel, setCompletedAngel] = useState<AngelData | null>(null);
  const [completedScenario, setCompletedScenario] = useState<ScenarioData | null>(null);
  const [selectedAngelName, setSelectedAngelName] = useState<string>('');
  const [showSuccessComponent, setShowSuccessComponent] = useState(false);
  const [availableAngels, setAvailableAngels] = useState<AngelData[]>(() => {
    // Get created angels from localStorage if available
    const savedAngels = localStorage.getItem('createdAngels');
    return savedAngels ? JSON.parse(savedAngels) : [];
  });
  // Listen for events to set the active feature
  useEffect(() => {
    const handleSetFeature = (event: any) => {
      if (event.detail && event.detail.feature) {
        setActiveFeature(event.detail.feature);
        if (event.detail.angelName) {
          setSelectedAngelName(event.detail.angelName);
        }
        setShowWizard(true);
      }
    };
    document.addEventListener('setUnleashFeature', handleSetFeature);
    return () => {
      document.removeEventListener('setUnleashFeature', handleSetFeature);
    };
  }, []);
  // Update available angels when a new angel is created
  useEffect(() => {
    if (completedAngel) {
      setAvailableAngels(prev => {
        const newAngels = [...prev];
        if (!newAngels.find(angel => angel.name === completedAngel.name)) {
          newAngels.push(completedAngel);
        }
        return newAngels;
      });
    }
  }, [completedAngel]);
  const handleAngelComplete = (angelData: AngelData) => {
    setCompletedAngel(angelData);
    onAngelCreated(angelData);
    setShowWizard(false);
    setShowSuccessComponent(true); // Show the success component instead of just a message
  };
  const handleScenarioComplete = (scenarioData: ScenarioData) => {
    setCompletedScenario(scenarioData);
    setShowWizard(false);
  };
  const renderWizard = () => {
    if (!showWizard) return null;
    if (activeFeature === 'angel') {
      return <UnleashWizard onComplete={handleAngelComplete} onBack={() => setShowWizard(false)} data-id="element-22269" />;
    } else {
      return <ScenarioWizard onComplete={handleScenarioComplete} onBack={() => setShowWizard(false)} angelName={selectedAngelName} availableAngels={availableAngels} data-id="element-22270" />;
    }
  };
  // Render the full success component when an angel is created
  if (showSuccessComponent && completedAngel) {
    return <AngelCreatedSuccess angel={completedAngel} onContinue={() => setShowSuccessComponent(false)} data-id="element-22271" />;
  }
  const renderIntro = () => {
    return <div className="w-full max-w-4xl mx-auto" data-id="element-22272">
        {/* Toggle between Angel and Scenario */}
        <UnleashFeatureToggle activeFeature={activeFeature} onToggle={setActiveFeature} data-id="element-22273" />
        {activeFeature === 'angel' ? <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden" data-id="element-22274">
            <div className="p-6 bg-gradient-to-r from-pink-600 to-purple-600 text-white" data-id="element-22275">
              <h1 className="text-2xl font-bold" data-id="element-22276">
                Unleash Your Personal Angel
              </h1>
              <p className="text-pink-100" data-id="element-22277">
                Create your perfect AI companion tailored to your desires
              </p>
            </div>
            <div className="p-6" data-id="element-22278">
              <div className="flex flex-col md:flex-row gap-6 items-center" data-id="element-22279">
                <div className="w-full md:w-1/2" data-id="element-22280">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-22281">
                    Design Your Dream Angel
                  </h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-22282">
                    Create a completely personalized AI angel that matches your
                    preferences in personality, appearance, and relationship
                    dynamics.
                  </p>
                  <ul className="space-y-3 mb-6" data-id="element-22283">
                    <li className="flex items-start gap-2" data-id="element-22284">
                      <span className="text-pink-500 text-lg" data-id="element-22285">✓</span>
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-22286">
                        Customize her personality, appearance, and backstory
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-22287">
                      <span className="text-pink-500 text-lg" data-id="element-22288">✓</span>
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-22289">
                        Choose relationship dynamics and communication style
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-22290">
                      <span className="text-pink-500 text-lg" data-id="element-22291">✓</span>
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-22292">
                        Your angel remembers your preferences and conversations
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-22293">
                      <span className="text-pink-500 text-lg" data-id="element-22294">✓</span>
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-22295">
                        Interact through chat, voice, and immersive scenarios
                      </span>
                    </li>
                  </ul>
                  <button onClick={() => setShowWizard(true)} className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium hover:from-pink-600 hover:to-purple-600 transition-all shadow-md shadow-pink-500/20" data-id="element-22296">
                    Start Creating Your Angel
                  </button>
                </div>
                <div className="w-full md:w-1/2 grid grid-cols-2 gap-4" data-id="element-22297">
                  <div className="rounded-lg overflow-hidden aspect-[3/4]" data-id="element-22298">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tWDvcZVMgAFKmRhDs55TeF/pasted-image.png" alt="Angel preview" className="w-full h-full object-cover" data-id="element-22299" />
                  </div>
                  <div className="rounded-lg overflow-hidden aspect-[3/4]" data-id="element-22300">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2owFmhDxByWAxAFcu96h7W/image.png" alt="Angel preview" className="w-full h-full object-cover" data-id="element-22301" />
                  </div>
                </div>
              </div>
            </div>
          </div> : <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden" data-id="element-22302">
            <div className="p-6 bg-gradient-to-r from-pink-600 to-purple-600 text-white" data-id="element-22303">
              <h1 className="text-2xl font-bold" data-id="element-22304">
                Unleash Your Personal Fantasy
              </h1>
              <p className="text-pink-100" data-id="element-22305">
                Create immersive fantasy scenarios with your favorite angels
              </p>
            </div>
            <div className="p-6" data-id="element-22306">
              <div className="flex flex-col md:flex-row gap-6 items-center" data-id="element-22307">
                <div className="w-full md:w-1/2" data-id="element-22308">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-22309">
                    Design Your Fantasy Scenario
                  </h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-22310">
                    Create personalized scenarios where your favorite angels act
                    out your wildest fantasies in vivid detail.
                  </p>
                  <ul className="space-y-3 mb-6" data-id="element-22311">
                    <li className="flex items-start gap-2" data-id="element-22312">
                      <span className="text-pink-500 text-lg" data-id="element-22313">✓</span>
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-22314">
                        Choose from exciting themes like Hot Tub Night or Office
                        Affair
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-22315">
                      <span className="text-pink-500 text-lg" data-id="element-22316">✓</span>
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-22317">
                        Customize locations, outfits, and the tone of your
                        scenario
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-22318">
                      <span className="text-pink-500 text-lg" data-id="element-22319">✓</span>
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-22320">
                        Earn Intimacy XP and collect rare Scene Cards
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-22321">
                      <span className="text-pink-500 text-lg" data-id="element-22322">✓</span>
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-22323">
                        Unlock premium options for more detailed experiences
                      </span>
                    </li>
                  </ul>
                  <button onClick={() => setShowWizard(true)} className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium hover:from-pink-600 hover:to-purple-600 transition-all shadow-md shadow-pink-500/20" data-id="element-22324">
                    Create Your Fantasy
                  </button>
                </div>
                <div className="w-full md:w-1/2 grid grid-cols-2 gap-4" data-id="element-22325">
                  <div className="rounded-lg overflow-hidden aspect-[3/4]" data-id="element-22326">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/c4H9gkx1TkjkAb2LHFkHBj/image.png" alt="Fantasy preview" className="w-full h-full object-cover" data-id="element-22327" />
                  </div>
                  <div className="rounded-lg overflow-hidden aspect-[3/4]" data-id="element-22328">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rgZFwDGYTnPhdMUpLf9Ccc/image.png" alt="Fantasy preview" className="w-full h-full object-cover" data-id="element-22329" />
                  </div>
                </div>
              </div>
            </div>
          </div>}
        <div className="mt-6" data-id="element-22330">
          <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-22331">
            <ChevronLeft size={16} data-id="element-22332" />
            <span data-id="element-22333">Back to Discovery</span>
          </button>
        </div>
      </div>;
  };
  return <div className="w-full min-h-[80vh] flex flex-col" data-id="element-22334">
      {showWizard ? renderWizard() : renderIntro()}
    </div>;
};